import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Box,
  Typography,
  Divider,
  Switch,
  styled
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Dashboard as DashboardIcon,
  Visibility as VisibilityIcon,
  Map as MapIcon,
  Group as GroupIcon,
  Description as TemplateIcon,
  Traffic as TrafficIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
  ViewSidebar as ViewSidebarIcon,
  Schedule as ScheduleIcon,
  Help as HelpIcon,
  Folder as FolderIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config';
import './Navbar.css';

const Navbar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'false');
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#ffffff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#f0f0f0');

  const [companyName, setCompanyName] = useState('Sign Blast' || '');

  const updateColors = (isDarkMode) => {
    if (isDarkMode) {
      setPrimaryColor('#0f0f0f');
      setSecondaryColor('#1a1a1a');
      localStorage.setItem('primaryColor', '#0f0f0f');
      localStorage.setItem('secondaryColor', '#1a1a1a');
      localStorage.setItem('darkMode', true);
    } else {
      setPrimaryColor('#f5f5f5');
      setSecondaryColor('#e0e0e0');
      localStorage.setItem('primaryColor', '#f5f5f5');
      localStorage.setItem('secondaryColor', '#e0e0e0');
      localStorage.setItem('darkMode', false);
    }
  };

  useEffect(() => {
      fetchUserDetails();
  }, []);
  
  const fetchUserDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const details = await response.json();
      setUserDetails(details);
      // updateColors
      updateColors(details.dark_mode === 1);
      setDarkMode(details.dark_mode === 1);
      
    } catch (error) {
      console.error('Error fetching time settings:', error);
    }
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    updateColors(savedDarkMode);
  }, []);

  const DarkModeSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
      margin: 1,
      padding: 0,
      transform: 'translateX(6px)',
      transition: 'transform 0.3s, background-color 0.3s',
      '&.Mui-checked': {
        color: '#fff',
        transform: 'translateX(22px)',
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            '#fff',
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: darkMode ? '#8796A5' : '#aab4be',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: darkMode ? '#003892' : '#ffd700',
      width: 32,
      height: 32,
      transition: 'transform 0.3s, background-color 0.3s',
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#000',
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
        transition: 'transform 0.3s',
      },
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      borderRadius: 10,
      transition: 'background-color 0.3s',
    },
  }));

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    updateColors(newDarkMode);
  };
  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleExitClick = () => {
    localStorage.clear();
    navigate('/landing');
  };

  const isActive = (path) => {
    if (path === '/overview') {
      return location.pathname.startsWith('/device') || location.pathname === '/overview';
    }

    return location.pathname.startsWith(path);
  };

  const navItems = [
    { text: 'DASHBOARD', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'OVERVIEW', icon: <VisibilityIcon />, path: '/overview' },
    { text: 'MAPS', icon: <MapIcon />, path: '/maps' },
    { text: 'GROUPS', icon: <GroupIcon />, path: '/groups' },
    { text: 'PRESETS', icon: <TemplateIcon />, path: '/presets' },
    { text: 'SCHEDULES', icon: <ScheduleIcon />, path: '/schedules' },
    { text: 'CALENDAR SCHEDULES', icon: <CalendarIcon />, path: '/calendar-schedules' },
    { text: 'TRAFFIC ANALYTICS', icon: <TrafficIcon />, path: '/traffic' },
    { text: 'FILE MANAGEMENT', icon: <FolderIcon />, path: '/file-management' },
  ];

  const bottomNavItems = [
    { text: 'SETTINGS', icon: <SettingsIcon />, path: '/settings' },
    { text: 'SUPPORT CENTER', icon: <HelpIcon />, path: '/help' }
  ];

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? 240 : 70,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 70,
          boxSizing: 'border-box',
          transition: 'width 0.3s, background-color 0.3s',
          backgroundColor: secondaryColor,
          color: darkMode ? '#ffffff' : '#333333',
          borderRadius: 0,
          borderRight: `10px solid ${secondaryColor}`, 
        },
      }}
    >
    <Divider />
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <IconButton 
          onClick={onToggle}
          sx={{
            backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            },
            transform: open ? 'none' : 'rotate(180deg)',
            transition: 'transform 0.3s',
            
          }}
        >
          <ViewSidebarIcon sx={{ color: darkMode ? '#ffffff' : '#333333'}}/>
        </IconButton>
      </Box>

        <Typography 
          sx={{ 
            fontSize: '1.5rem',
            fontWeight: 500,
            color: 'inherit',
            textAlign: 'center',
            '@media (max-width: 600px)': {
              fontSize: '1.2rem',
              textAlign: 'center',
            }
          }}
        >
          {userDetails?.org_name}        
        </Typography>

      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              borderRadius: '0 10px 10px 0',
              marginRight: '10px',
              mr: 1,
              mb: 1,
              backgroundColor: isActive(item.path) ? '#4CAF50' : 'transparent',
              '&:hover': {
                backgroundColor: isActive(item.path) ? '#45a049' : 'rgba(0, 0, 0, 0.04)',
              },
              position: 'relative', 
              top: 0, 
            }}
          >
            <ListItemIcon sx={{ color: darkMode ? '#ffffff' : '#333333', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            {open && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem', color: darkMode ? '#ffffff' : '#333333' }} />}
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {bottomNavItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              borderRadius: '0 10px 10px 0',
              marginRight: '10px',
              mr: 1,
              mb: 1,
              backgroundColor: isActive(item.path) ? '#4CAF50' : 'transparent',
              '&:hover': {
                backgroundColor: isActive(item.path) ? '#45a049' : 'rgba(0, 0, 0, 0.04)',
              },
              position: 'relative', // Ensure the list item is positioned relative to its container
              top: 0, // Ensure the list item stays at the top
            }}
          >
            <ListItemIcon sx={{ color: darkMode ? '#ffffff' : '#333333', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            {open && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem', color: darkMode ? '#ffffff' : '#333333' }} />}
          </ListItem>
        ))}
      </List>
      
      <Box sx={{ flexGrow: 1 }} />
      <ListItem 
        sx={{
          justifyContent: open ? 'space-between' : 'center',
          px: open ? 2 : 1,
        }}
      >
        {open && (
          <ListItemText 
            primary="Dark Mode" 
            primaryTypographyProps={{ fontSize: '0.9rem' }}
          />
        )}
        <DarkModeSwitch
          checked={darkMode}
          onChange={toggleDarkMode}
          inputProps={{ 'aria-label': 'toggle dark mode' }}
        />
      </ListItem>
      <Divider />
      <List>
        <ListItem 
          button 
          onClick={handleExitClick}
          sx={{
            justifyContent: open ? 'flex-start' : 'center',
            px: open ? 2 : 1,
          }}
        >
          <ListItemIcon sx={{ color: darkMode ? 'white' : 'inherit', minWidth: open ? 40 : 'auto' }}>
            <ExitToAppIcon />
          </ListItemIcon>
          {open && <ListItemText primary="SIGN OUT" primaryTypographyProps={{ fontSize: '0.9rem' }} />}
        </ListItem>
      </List>
    </Drawer>
  );
};

export default Navbar;